/**
 * ErrorBoundary
 *
 * Top-level React error boundary so that an uncaught render error in any page
 * doesn't blank-screen the entire app. Renders a friendly fallback UI with a
 * "Reload" CTA.
 *
 * Notes:
 *   - This catches errors during rendering, in lifecycle methods, and in
 *     constructors of the whole tree below us.
 *   - It does NOT catch errors in event handlers, async callbacks, or
 *     server-side rendering. Those still need try/catch.
 *   - In production, error details are NOT shown to the user. In dev (when
 *     NODE_ENV !== "production"), we print the stack to the page so engineers
 *     can debug fast.
 */
import { Component } from "react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback.
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to the browser console for now; swap to Sentry/etc. when available.
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    // Try a soft reset first; if that doesn't bring the app back, hard reload.
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-gray-50">
        <div className="max-w-lg text-center" data-testid="error-boundary-fallback">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-red-500 mb-4 text-3xl">
            !
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-600 mb-6">
            We hit an unexpected error rendering this page. Reloading usually fixes it. If the problem persists, please contact support.
          </p>
          <button
            onClick={this.handleReload}
            className="px-5 py-2.5 rounded-lg bg-black text-white font-semibold hover:bg-gray-800 transition-colors"
            data-testid="error-boundary-reload"
          >
            Reload page
          </button>

          {isDev && this.state.error && (
            <details className="mt-8 text-left text-xs text-gray-500 bg-white p-4 rounded-lg border border-gray-200">
              <summary className="cursor-pointer font-semibold mb-2">Error details (dev only)</summary>
              <pre className="whitespace-pre-wrap break-words">{String(this.state.error?.stack || this.state.error)}</pre>
              {this.state.info?.componentStack && (
                <pre className="mt-3 whitespace-pre-wrap break-words text-gray-400">
                  {this.state.info.componentStack}
                </pre>
              )}
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
